import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useNotification } from '../../context/NotificationContext';
import ThemedText from '../common/ThemedText';
import AppLogo from '../common/AppLogo';
import NotificationIcon from '../icons/NotificationIcon';
import ChatIcon from '../icons/ChatIcon';
import ProfileDrawer from './ProfileDrawer';

type TopBarNavigationProp = StackNavigationProp<RootStackParamList>;

interface TopBarProps {
  title?: string;
  showLogo?: boolean;
  showBackButton?: boolean;
  onBackPress?: () => void;
  rightActions?: React.ReactNode;
}

const TopBar: React.FC<TopBarProps> = ({ title, showLogo = false, showBackButton = false, onBackPress, rightActions }) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<TopBarNavigationProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { totalUnreadChats } = useChat();
  const { unreadCount } = useNotification();
  const [drawerVisible, setDrawerVisible] = useState(false);

  return (
    <>
      <View style={[styles.container, { 
        paddingTop: insets.top,
        backgroundColor: theme.background,
        borderBottomColor: theme.border,
        shadowColor: theme.shadow,
      }]}>
        <View style={styles.content}>
          <View style={styles.leftSection}>
            {showBackButton ? (
              <TouchableOpacity
                style={styles.backButton}
                onPress={onBackPress || (() => navigation.goBack())}
                activeOpacity={0.7}
              >
                <Ionicons name="arrow-back" size={28} color={theme.text} />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={styles.profileButton}
                onPress={() => setDrawerVisible(true)}
                activeOpacity={0.7}
              >
                {(user as any)?.photo ? (
                  <Image 
                    source={{ uri: (user as any).photo }} 
                    style={styles.profileButtonImage}
                  />
                ) : (
                  <Ionicons name="person-circle" size={36} color={theme.text} />
                )}
              </TouchableOpacity>
            )}
          </View>

        <View style={styles.centerSection}>
          {showLogo ? (
            <AppLogo width={98} height={33} />
          ) : (
            <ThemedText style={[styles.title, { color: theme.text }]}>{title}</ThemedText>
          )}
        </View>

        <View style={styles.rightIcons}>
          {rightActions || (
            <>
              <TouchableOpacity
                style={styles.iconButton}
                onPress={() => navigation.navigate('Notification')}
                activeOpacity={0.7}
              >
                <NotificationIcon size={28} color={theme.text} />
                {unreadCount > 0 && (
                  <View style={[styles.badge, { backgroundColor: '#EF4444' }]}>
                    <ThemedText style={styles.badgeText}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </ThemedText>
                  </View>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.iconButton}
                onPress={() => navigation.navigate('Chats', {})}
                activeOpacity={0.7}
              >
                <ChatIcon size={26} color={theme.text} />
                {totalUnreadChats > 0 && (
                  <View style={[styles.badge, { backgroundColor: '#EF4444' }]}>
                    <ThemedText style={styles.badgeText}>
                      {totalUnreadChats > 99 ? '99+' : totalUnreadChats}
                    </ThemedText>
                  </View>
                )}
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </View>
    
    <ProfileDrawer 
      visible={drawerVisible} 
      onClose={() => setDrawerVisible(false)} 
    />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  content: {
    height: 60,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  leftSection: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  centerSection: {
    flex: 2,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightIcons: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
  },
  profileButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileButtonImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: '#D4AF37',
  },
  iconButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
});

export default TopBar;
